import { useState } from 'react';

import { useNavigate } from 'react-router';

import { createColumnHelper } from '@tanstack/react-table';
import {
  ArrowUturnDownIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { Card } from '../../../components/base/Card';
import { DataTable } from '../../../components/base/Table';
import { useClipboardCopy } from '../../../hooks/useClipboardCopy';
import { ConfirmDelete } from '../../../components/shared/ConfirmDelete';
import { useGlobalHardwareModelFilter } from '../../../contexts/GlobalHardwareFilterContext';
import {
  FaultDefinitionCategoryNames,
  IFaultDefinition,
} from '../../../types/Fault/FaultDefinition';
import useSuperAdminFaultDefinitionMutations from '../../../data/fault/superadmin/useSuperAdminFaultDefinitionMutations';
import { faultQueryKeys } from '../../../data/QueryKeys';
import { Button } from '../../../components/base/Button';

const colHelper = createColumnHelper<IFaultDefinition>();

export function FaultDefinitionsPage() {
  const { CopyToClipboardButton } = useClipboardCopy();
  const navigate = useNavigate();
  const { delFaultDefinition, updateFaultDefinition } =
    useSuperAdminFaultDefinitionMutations();
  const { globalHardwareModelFilter } = useGlobalHardwareModelFilter();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteFaultDefinition, setDeleteFaultDefinition] = useState<
    IFaultDefinition | undefined
  >(undefined);

  const handleDeleteFaultDefinition = (faultDefinition: IFaultDefinition) => {
    setDeleteFaultDefinition(faultDefinition);
    setDeleteOpen(true);
  };

  const handleConfirmDeleteFaultDefinition = () => {
    delFaultDefinition.mutate(deleteFaultDefinition!);
    setDeleteOpen(false);
  };

  const handleRestoreFaultDefinition = (faultDefinition: IFaultDefinition) => {
    updateFaultDefinition.mutate({ ...faultDefinition, restore: true });
  };

  const cols = [
    colHelper.accessor('title', { header: 'Title' }),
    colHelper.accessor((row) => FaultDefinitionCategoryNames[row.category], {
      header: 'Category',
    }),
    colHelper.accessor('value', { header: 'Value' }),
    colHelper.accessor('code', { header: 'Code' }),
    colHelper.display({
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          {info.cell.row.original.deletedOn ? (
            <ArrowUturnDownIcon
              className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
              onClick={() => handleRestoreFaultDefinition(info.row.original)}
            />
          ) : (
            <>
              <CopyToClipboardButton iconOnly copyText={info.row.original.id} />
              <PencilSquareIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => navigate(info.row.original.id)}
              />
              <TrashIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleDeleteFaultDefinition(info.row.original)}
              />
            </>
          )}
        </div>
      ),
    }),
  ];

  return (
    <>
      <ConfirmDelete
        itemName={'Fault Definition'}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => handleConfirmDeleteFaultDefinition()}
      />
      <Card title="Fault Definitions">
        <div className="my-6 space-x-4">
          <Button onClick={() => navigate('add')}>Add Fault Definition</Button>
        </div>
        <div>
          <DataTable<IFaultDefinition>
            url="/superadmin/fault/definitions"
            queryKey={faultQueryKeys.superAdminDefinitions()}
            columns={cols}
            predefinedFilterFields={
              globalHardwareModelFilter
                ? [
                    {
                      field: 'hardwareModelId',
                      value: globalHardwareModelFilter.id,
                      fieldLabel: 'Hardware Model',
                      valueLabel: globalHardwareModelFilter.name,
                    },
                  ]
                : undefined
            }
            enableShowDeleted
          />
        </div>
      </Card>
    </>
  );
}
